import { isElement } from "./periodic-table";

export interface ParseOk {
  ok: true;
  counts: Record<string, number>;
}
export interface ParseErr {
  ok: false;
  error: string;
}
export type ParseResult = ParseOk | ParseErr;

// Parse one group (no hydrate dots) into element counts using a scope stack for
// nested parentheses/brackets.
function parseGroup(s: string): ParseResult {
  const stack: Record<string, number>[] = [{}];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === "(" || c === "[") {
      stack.push({});
      i++;
      continue;
    }
    if (c === ")" || c === "]") {
      i++;
      let num = "";
      while (i < s.length && /\d/.test(s[i])) {
        num += s[i];
        i++;
      }
      const mult = num ? Number.parseInt(num, 10) : 1;
      const top = stack.pop();
      if (!top || stack.length === 0)
        return { ok: false, error: "Unbalanced parentheses." };
      const parent = stack[stack.length - 1];
      for (const [el, n] of Object.entries(top))
        parent[el] = (parent[el] ?? 0) + n * mult;
      continue;
    }
    if (/[A-Z]/.test(c)) {
      let sym = c;
      i++;
      while (i < s.length && /[a-z]/.test(s[i])) {
        sym += s[i];
        i++;
      }
      if (!isElement(sym))
        return { ok: false, error: `Unknown element: ${sym}` };
      let num = "";
      while (i < s.length && /\d/.test(s[i])) {
        num += s[i];
        i++;
      }
      const cnt = num ? Number.parseInt(num, 10) : 1;
      const top = stack[stack.length - 1];
      top[sym] = (top[sym] ?? 0) + cnt;
      continue;
    }
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    return { ok: false, error: `Unexpected character: ${c}` };
  }
  if (stack.length !== 1)
    return { ok: false, error: "Unbalanced parentheses." };
  return { ok: true, counts: stack[0] };
}

/**
 * Parse a chemical formula (e.g. `C6H12O6`, `Ca(OH)2`, `CuSO4·5H2O`) into total
 * element counts. Hydrate separators (·, •, *, .) split segments; a leading
 * integer on a segment multiplies that whole segment.
 */
export function parseFormula(input: string): ParseResult {
  const formula = input.trim();
  if (!formula) return { ok: false, error: "Enter a chemical formula." };

  const segments = formula
    .split(/[·•*.]/)
    .filter((seg) => seg.trim().length > 0);
  if (segments.length === 0)
    return { ok: false, error: "Enter a chemical formula." };

  const total: Record<string, number> = {};
  for (const seg of segments) {
    const lead = seg.match(/^(\d+)(.*)$/);
    const coeff = lead ? Number.parseInt(lead[1], 10) : 1;
    const body = lead ? lead[2] : seg;
    const parsed = parseGroup(body);
    if (!parsed.ok) return parsed;
    for (const [el, n] of Object.entries(parsed.counts))
      total[el] = (total[el] ?? 0) + n * coeff;
  }

  if (Object.keys(total).length === 0)
    return { ok: false, error: "No elements found." };
  return { ok: true, counts: total };
}
